import { match } from 'ts-pattern'
import {
  type BlockedScaOutcome,
  type BlockedTriageOutcome,
  type ExcludedScaOutcome,
  type ExcludedTriageOutcome,
  type ScaOutcome,
  type TriageOutcome,
} from '../../utils/api-client/api-schemas.ts'
import { type OutcomeBannerOutcome } from '../activity-feed/outcome-banner.tsx'
import { type OutcomeType } from './outcome-types.ts'

/**
 * True when an SCA outcome is blocked or excluded — the two outcomes that carry no article/report
 * and instead surface their own `reason`/`details`. Generic over the input union so it narrows
 * whether the caller holds a `ScaOutcome` or the broader `OutcomeBannerOutcome`, while rejecting a
 * triage outcome (constraining `T` keeps the mirror-image `isBlockedOrExcludedTriageOutcome` mix-up
 * from silently compiling to an always-false check).
 */
export const isBlockedOrExcludedScaOutcome = <T extends ScaOutcome | OutcomeBannerOutcome>(
  outcome: T | null | undefined
): outcome is Extract<T, BlockedScaOutcome | ExcludedScaOutcome> =>
  outcome?.type === 'blocked-sca' || outcome?.type === 'excluded-sca'

/**
 * True when a triage outcome is blocked or excluded — see {@link isBlockedOrExcludedScaOutcome}.
 */
export const isBlockedOrExcludedTriageOutcome = <T extends TriageOutcome | OutcomeBannerOutcome>(
  outcome: T | null | undefined
): outcome is Extract<T, BlockedTriageOutcome | ExcludedTriageOutcome> =>
  outcome?.type === 'blocked-triage' || outcome?.type === 'excluded-triage'

export const getOutcomeLabel = (outcomeType: OutcomeType): string =>
  match(outcomeType)
    .with('true-positive', () => 'True positive')
    .with('likely-true-positive', () => 'Likely true positive')
    .with('inconclusive', () => 'Inconclusive')
    .with('potentially-exploitable', () => 'Potentially exploitable')
    .with('no-fix-necessary', () => 'No fix necessary')
    .with('no-fix-necessary-false-positive', () => 'False positive')
    .with('no-fix-necessary-wont-fix', () => "Won't fix")
    .with('no-fix-necessary-not-exploitable', () => 'Not exploitable')
    .with('error', () => 'Error')
    .with('blocked', () => 'Blocked')
    .with('excluded', () => 'Excluded')
    .with('exploitable', () => 'Exploitable')
    .exhaustive()
